import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { SEARCH_FILTER_PARAMS } from '@/app/common/filters/core/filterDefinitions.ts';
import type { StarTrack } from '@/app/common/starTrack/startrack.ts';
import type { ResultPageFilterTracking } from '@/app/common/starTrack/events/resultPageFilter.ts';
import {
  createResultPageFilterPayload,
  RESULT_PAGE_FILTER_TRIGGER_SECTION,
} from '@/app/common/starTrack/events/resultPageFilter.ts';
import type { Localization } from '@/app/components/search/common/Localization';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { normalizeEvents } from '../../Analytics/utils';
import getSearchParams from '../../common/getSearchParams';

const isFilterName = (name: string) => SEARCH_FILTER_PARAMS.find((filterName) => filterName === name);

export const removeSingleFilter = (params: SearchParams, name: string): SearchParams => {
  const paramsCopy = { ...params };
  if (name === URLParameters.ACCOMODATION_FACILITIES) {
    delete paramsCopy?.[URLParameters.GENERAL_ACCOMODATION_FACILITIES];
    delete paramsCopy?.[URLParameters.FAMILY_ACCOMODATION_FACILITIES];
    delete paramsCopy?.[URLParameters.ACTIVITY_ACCOMODATION_FACILITIES];
    delete paramsCopy?.[URLParameters.WELLNESS_ACCOMODATION_FACILITIES];
    delete paramsCopy?.[URLParameters.FOOD_ACCOMODATION_FACILITIES];
  }
  delete paramsCopy?.[name];
  return paramsCopy;
};

type RemoveAllFiltersOptions = {
  exclude?: URLParameters[];
};

export const removeAllFilters = (params: SearchParams, { exclude = [] }: RemoveAllFiltersOptions = {}): SearchParams =>
  Object.entries(params).reduce((result, [key, value]) => {
    const shouldRemove = isFilterName(key) && !exclude.includes(key as URLParameters);
    return shouldRemove ? result : { ...result, [key]: value };
  }, {});

export const removeSearchParams = (params: SearchParams): SearchParams =>
  Object.entries(params).reduce((result, [key, value]) => {
    const param = !isFilterName(key) ? {} : { [key]: value };
    return { ...result, ...param };
  }, {});

export const removeEmptyParams = (params: SearchParams): SearchParams =>
  Object.entries(params).reduce((result, [key, value]) => {
    const param = value !== undefined && value !== null && value !== '' ? { [key]: value } : {};
    return { ...result, ...param };
  }, {});

export const countFilters = (query?: SearchParams): { count: number } => {
  let count = 0;
  if (canUseDOM) {
    const queryStringParams = new URLSearchParams(window.location.search);
    queryStringParams.forEach((value, key) => {
      if (isFilterName(key)) count += 1;
    });
  } else if (query) {
    Object.keys(query).forEach((key) => {
      if (isFilterName(key)) count += 1;
    });
  }
  return { count };
};

export const getFiltersFromQueryString = (searchParams: SearchParams): SearchParams =>
  Object.entries(searchParams).reduce(
    (result, [key, val]) =>
      SEARCH_FILTER_PARAMS.find((filterName) => key === filterName) ? { ...result, [key]: val } : result,
    {}
  );

type ApplyResultPageFilter = {
  oldFilters: SearchParams;
  appliedFilters: SearchParams;
  searchParams: SearchParams;
  starTrack: StarTrack;
  localization: Localization;
  tracking?: ResultPageFilterTracking;
};

// Only the quick filters shown on the result page report RESULT_PAGE; every other surface (All filters
// modal, pill overlays, badges, sidebar, legacy filters) is the standard filter UI.
const STANDARD_UI_TRACKING: ResultPageFilterTracking = {
  triggerSection: RESULT_PAGE_FILTER_TRIGGER_SECTION.POP_UP,
};

export const applyResultPageFilterEvent = ({
  oldFilters,
  appliedFilters,
  searchParams,
  starTrack,
  localization,
  tracking = STANDARD_UI_TRACKING,
}: ApplyResultPageFilter) => {
  const { triggerSection, highlighted = {} } = tracking;
  Object.keys(appliedFilters).forEach((x) => {
    if (appliedFilters[x] !== oldFilters[x]) {
      starTrack.resultPageFilter(
        createResultPageFilterPayload({
          searchParams,
          filterName: normalizeEvents(x).event_name,
          value: appliedFilters[x],
          deselectFlag: !!oldFilters[x],
          filterType: highlighted[x] ?? null,
          triggerSection,
          localization,
        })
      );
    }
  });
  Object.keys(oldFilters).forEach((x) => {
    if (!appliedFilters[x]) {
      starTrack.resultPageFilter(
        createResultPageFilterPayload({
          searchParams,
          filterName: normalizeEvents(x).event_name,
          value: oldFilters[x],
          deselectFlag: true,
          filterType: highlighted[x] ?? null,
          triggerSection,
          localization,
        })
      );
    }
  });
};

export const getFiltersCount = () => {
  const searchParamsFiltersCount = getSearchParams({
    exclude: [
      URLParameters.CUSTOM_CHECKIN_CHECKOUT,
      URLParameters.FILTER_OPX_FLEXIBLE,
      URLParameters.GENERAL_ACCOMODATION_FACILITIES,
      URLParameters.FAMILY_ACCOMODATION_FACILITIES,
      URLParameters.ACTIVITY_ACCOMODATION_FACILITIES,
      URLParameters.WELLNESS_ACCOMODATION_FACILITIES,
      URLParameters.FOOD_ACCOMODATION_FACILITIES,
    ],
  });
  const appliedFilters = removeSearchParams(searchParamsFiltersCount);
  return Object.values(appliedFilters).filter((v) => v !== '0').length;
};
